import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import './QuotesJournal.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function QuotesJournal({ factFictionMode }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [dailyQuote, setDailyQuote] = useState(null);
  const [journal, setJournal] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Quote form
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [quoteBookTitle, setQuoteBookTitle] = useState('');
  const [quotePage, setQuotePage] = useState('');
  const [quoteTags, setQuoteTags] = useState('');
  const [savingQuote, setSavingQuote] = useState(false);

  // Journal form
  const [showJournalForm, setShowJournalForm] = useState(false);
  const [journalBookTitle, setJournalBookTitle] = useState('');
  const [journalContent, setJournalContent] = useState('');
  const [savingJournal, setSavingJournal] = useState(false);

  // Quote filter
  const [quoteFilter, setQuoteFilter] = useState('');

  const fetchQuotes = useCallback(async () => {
    try {
      const [myQuotes, daily] = await Promise.all([
        apiFetchJson(instance, '/api/quotes/my'),
        apiFetchJson(instance, '/api/quotes/daily').catch(() => null),
      ]);
      setQuotes(unwrap(myQuotes));
      setDailyQuote(daily);
    } catch (err) {
      setError(err.message || 'Failed to load quotes');
    }
  }, [instance]);

  const fetchJournal = useCallback(async () => {
    const promptType = factFictionMode === 'fact' ? 'fact' : 'fiction';
    try {
      const [entries, promptData] = await Promise.all([
        apiFetchJson(instance, '/api/journal'),
        apiFetchJson(instance, `/api/journal/prompts?type=${promptType}`).catch(() => []),
      ]);
      setJournal(unwrap(entries));
      setPrompts(unwrap(promptData));
    } catch (err) {
      setError(err.message || 'Failed to load journal');
    }
  }, [instance, factFictionMode]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([fetchQuotes(), fetchJournal()]).finally(() => setLoading(false));
  }, [fetchQuotes, fetchJournal]);

  const handleSaveQuote = async (e) => {
    e.preventDefault();
    if (!quoteText.trim()) return;
    setSavingQuote(true);
    try {
      await apiFetchJson(instance, '/api/quotes', {
        method: 'POST',
        body: JSON.stringify({
          text: quoteText.trim(),
          bookTitle: quoteBookTitle.trim() || undefined,
          pageReference: quotePage.trim() || undefined,
          tags: quoteTags.split(',').map(t => t.trim()).filter(Boolean),
        }),
      });
      setQuoteText('');
      setQuoteBookTitle('');
      setQuotePage('');
      setQuoteTags('');
      setShowQuoteForm(false);
      fetchQuotes();
    } catch (err) {
      setError(err.message || 'Failed to save quote');
    } finally {
      setSavingQuote(false);
    }
  };

  const handleSaveJournal = async (e) => {
    e.preventDefault();
    if (!journalContent.trim()) return;
    setSavingJournal(true);
    try {
      await apiFetchJson(instance, '/api/journal', {
        method: 'POST',
        body: JSON.stringify({
          bookTitle: journalBookTitle.trim() || undefined,
          content: journalContent.trim(),
        }),
      });
      setJournalBookTitle('');
      setJournalContent('');
      setShowJournalForm(false);
      fetchJournal();
    } catch (err) {
      setError(err.message || 'Failed to save journal entry');
    } finally {
      setSavingJournal(false);
    }
  };

  if (loading) return <div className="qj-loading">{t('bookClub.quotesJournal.loading')}</div>;
  if (error) return <div className="qj-error">{error} <button onClick={() => { fetchQuotes(); fetchJournal(); }}>{t('bookClub.common.retry')}</button></div>;

  const filteredQuotes = quoteFilter
    ? quotes.filter(q =>
        (q.bookTitle || '').toLowerCase().includes(quoteFilter.toLowerCase()) ||
        (q.tags || []).some(t => t.toLowerCase().includes(quoteFilter.toLowerCase()))
      )
    : quotes;

  const randomPrompt = prompts.length > 0 ? prompts[Math.floor(Math.random() * prompts.length)] : null;

  return (
    <div className="qj-container">
      {/* Quote of the Day */}
      {dailyQuote && (
        <div className="qj-daily-banner">
          <span className="qj-daily-label">✨ {t('bookClub.quotesJournal.quoteOfTheDay')}</span>
          <blockquote className="qj-daily-text">"{dailyQuote.text || dailyQuote.quote}"</blockquote>
          {dailyQuote.author && <span className="qj-daily-author">— {dailyQuote.author}</span>}
        </div>
      )}

      {/* Section tabs */}
      <div className="qj-tabs">
        <button
          className={`qj-tab ${activeSection === 'quotes' ? 'active' : ''}`}
          onClick={() => setActiveSection('quotes')}
        >
          💬 {t('bookClub.quotesJournal.myQuotes')}
        </button>
        <button
          className={`qj-tab ${activeSection === 'journal' ? 'active' : ''}`}
          onClick={() => setActiveSection('journal')}
        >
          📓 {t('bookClub.quotesJournal.readingJournal')}
        </button>
      </div>

      {/* Quotes section */}
      {activeSection === 'quotes' && (
        <div className="qj-section">
          <div className="qj-section-header">
            <input
              type="text"
              className="qj-filter-input"
              placeholder={t('bookClub.quotesJournal.filterPlaceholder')}
              value={quoteFilter}
              onChange={e => setQuoteFilter(e.target.value)}
            />
            <button className="qj-add-btn" onClick={() => setShowQuoteForm(!showQuoteForm)}>
              {showQuoteForm ? t('bookClub.common.cancel') : '+ ' + t('bookClub.quotesJournal.addQuote')}
            </button>
          </div>

          {showQuoteForm && (
            <form className="qj-form" onSubmit={handleSaveQuote}>
              <textarea
                className="qj-textarea"
                placeholder={t('bookClub.quotesJournal.quotePlaceholder')}
                value={quoteText}
                onChange={e => setQuoteText(e.target.value)}
                rows={3}
                required
              />
              <input
                type="text"
                className="qj-input"
                placeholder={t('bookClub.quotesJournal.bookTitle')}
                value={quoteBookTitle}
                onChange={e => setQuoteBookTitle(e.target.value)}
              />
              <div className="qj-form-row">
                <input
                  type="text"
                  className="qj-input"
                  placeholder={t('bookClub.quotesJournal.page')}
                  value={quotePage}
                  onChange={e => setQuotePage(e.target.value)}
                />
                <input
                  type="text"
                  className="qj-input"
                  placeholder={t('bookClub.quotesJournal.tags')}
                  value={quoteTags}
                  onChange={e => setQuoteTags(e.target.value)}
                />
              </div>
              <button type="submit" className="qj-save-btn" disabled={savingQuote}>
                {savingQuote ? t('bookClub.quotesJournal.saving') : t('bookClub.quotesJournal.saveQuote')}
              </button>
            </form>
          )}

          <div className="qj-quotes-list">
            {filteredQuotes.length === 0 ? (
              <p className="qj-empty">{t('bookClub.quotesJournal.noQuotes')}</p>
            ) : (
              filteredQuotes.map(q => (
                <div key={q.id} className="qj-quote-card">
                  <blockquote className="qj-quote-text">"{q.text || q.quote}"</blockquote>
                  <div className="qj-quote-meta">
                    {q.bookTitle && <span className="qj-quote-book">{q.bookTitle}</span>}
                    {q.pageReference && <span className="qj-quote-page">{q.pageReference}</span>}
                  </div>
                  {q.tags && q.tags.length > 0 && (
                    <div className="qj-quote-tags">
                      {(Array.isArray(q.tags) ? q.tags : []).map((tag, i) => (
                        <span key={i} className="qj-tag-chip">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Journal section */}
      {activeSection === 'journal' && (
        <div className="qj-section">
          <div className="qj-section-header">
            <span className="qj-section-label">{t('bookClub.quotesJournal.reflections')}</span>
            <button className="qj-add-btn" onClick={() => setShowJournalForm(!showJournalForm)}>
              {showJournalForm ? t('bookClub.common.cancel') : '+ ' + t('bookClub.quotesJournal.newEntry')}
            </button>
          </div>

          {showJournalForm && (
            <form className="qj-form" onSubmit={handleSaveJournal}>
              {randomPrompt && (
                <div className="qj-prompt-chip">
                  💡 Prompt: {randomPrompt.text || randomPrompt.prompt || randomPrompt}
                </div>
              )}
              <input
                type="text"
                className="qj-input"
                placeholder={t('bookClub.quotesJournal.bookTitleOptional')}
                value={journalBookTitle}
                onChange={e => setJournalBookTitle(e.target.value)}
              />
              <textarea
                className="qj-textarea"
                placeholder={t('bookClub.quotesJournal.thoughtsPlaceholder')}
                value={journalContent}
                onChange={e => setJournalContent(e.target.value)}
                rows={5}
                required
              />
              <button type="submit" className="qj-save-btn" disabled={savingJournal}>
                {savingJournal ? t('bookClub.quotesJournal.saving') : t('bookClub.quotesJournal.saveEntry')}
              </button>
            </form>
          )}

          <div className="qj-journal-list">
            {journal.length === 0 ? (
              <p className="qj-empty">{t('bookClub.quotesJournal.noJournalEntries')}</p>
            ) : (
              journal.map(entry => (
                <div key={entry.id} className="qj-journal-entry">
                  <div className="qj-journal-date">
                    {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : ''}
                  </div>
                  {entry.bookTitle && <div className="qj-journal-book">{entry.bookTitle}</div>}
                  <div className="qj-journal-content">{entry.content}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default QuotesJournal;
